import type {TFunction} from 'i18next';
import {SectorType} from 'crqlar-react-common/dist/models/sector/sector.model';
import {useAllSectors} from 'crqlar-react-common/dist/utils/hooks/sectors/useSectors';
import {useTranslation} from 'react-i18next';
import {useIsFetching} from 'react-query';

import {IdFilterSelection, StoredIdFilter} from '../../../config/reservationTriggers';
import {MultiSelect} from '../../../../../common/newSelect';
import type {SelectOption} from '../../../../../common/newSelect';

export const SECTOR_TYPE_LABEL_KEYS: Record<SectorType, string> = {
    [SectorType.ACCOMMODATION]: 'Accommodation',
    [SectorType.RESTAURANT]: 'Restaurant',
    [SectorType.SPA]: 'Spa',
    [SectorType.WEEKLY_PLANNER]: 'Weekly Planner',
    [SectorType.OTHER]: 'Other'
};

export const sectorTypeLabel = (sectorType: SectorType | undefined, t: TFunction): string =>
    t(sectorType ? (SECTOR_TYPE_LABEL_KEYS[sectorType] ?? 'Other') : 'Other');

interface SectorMultiSelectFieldProps {
    label: string;
    /** The stored filter: its name snapshots label ids the catalogue lacks. */
    filter: StoredIdFilter;
    onChange: (selection: IdFilterSelection[]) => void;
    disabled?: boolean;
}

/**
 * The sectors multi-select, grouped by type; empty = any sector. `useAllSectors` returns only the list, so
 * loading comes from the query cache and is passed as `x || undefined` (before the query starts it reads false).
 */
const SectorMultiSelectField = ({label, filter, onChange, disabled}: SectorMultiSelectFieldProps) => {
    const {t} = useTranslation();
    const sectors = useAllSectors();
    const loading = useIsFetching('allSectors') > 0;

    // Flat until at least one sector carries a type, rather than one "Other" heading over everything.
    const anyTyped = sectors.some(sector => !!sector.sectorType);

    // The select keeps the order it is handed: type headings A to Z, sectors A to Z inside them.
    const options: SelectOption[] = sectors
        .map(sector => ({
            value: sector.id!,
            label: sector.name || '',
            group: anyTyped ? sectorTypeLabel(sector.sectorType, t) : undefined
        }))
        .sort((left, right) => (left.group ?? '').localeCompare(right.group ?? '') || left.label.localeCompare(right.label));

    return (
        <MultiSelect
            label={label}
            placeholder={t('Any sector')}
            value={filter.ids}
            options={options}
            unknownLabels={filter.names}
            loading={loading || undefined}
            onChange={ids => onChange(ids.map(id => ({
                id,
                name: sectors.find(sector => sector.id === id)?.name || filter.names[id]
            })))}
            disabled={disabled}
        />
    );
};

export default SectorMultiSelectField;
